// `venue` moduli — captive portal API kontrakti.
// Manba (taxmin emas, o'qilgan): `xsib-core/venue/src/main/java/uz/xsib/venue/
// internal/GuestPortalController.java` va `venue/api/*.java` (GuestAccessRequest,
// GuestAccessResult, OtpChallenge, GuestSession, AdCampaign, AdImpression,
// GuestCredential, IdentificationMethod, IdentificationStrength).
//
// ⚠️ Bu ilova ISHONCHSIZ zonada ishlaydi (`docs/00-ARCHITECTURE.md` §8):
// hech qanday token yo'q, endpointlar ochiq. Shuning uchun HAR bir javob
// Zod orqali tekshiriladi — backend shaklini o'zgartirsa, ilova "nimadir
// noto'g'ri" deb jimgina ishlashda davom etish o'rniga ANIQ xato beradi.
//
// =====================================================================
// ⚠️⚠️ BU FAYLDA `.nullable()` ISHLATMANG — HAR DOIM `.nullish()`.
//
// Backendning HAMMA servisida `default-property-inclusion: non_null`
// yoqilgan: `null` maydon JSON'ga UMUMAN CHIQMAYDI. Ya'ni maydon yo
// qiymat bilan keladi, yo BUTUNLAY YO'Q — "null bilan keldi" holati
// hech qachon bo'lmaydi. `nullable()` esa aynan o'sha bo'lmaydigan
// holatni kutadi va maydon yo'qligida SXEMA YIQILADI.
//
// Bu nazariy emas. 2026-08-08 da mehmon portalining vaucher yo'li
// boshidan oxirigacha o'tkazilganda: karta QABUL QILINDI, backend
// sessiyani yaratdi — lekin `session.endedAt` (tugamagan sessiyada
// tabiiy ravishda yo'q) sxemani yiqitdi va mehmon ekranida internet
// o'rniga XOM ZOD XATOSI turdi. Ya'ni mehmon pul to'lagan, kirish
// berilgan, ekranda esa JSON.
//
// Xuddi shu tuzoq kabinetda ikki marta bo'lgan (sessiyalar ro'yxati
// va paket sotib olish). Uchinchisi shu yerda edi.
// =====================================================================

import { z } from 'zod'
import { api } from '@/shared/api/client'
// ⚠️ Sof (bog'liqliksiz) mantiq alohida modullarda: bu fayl `axios` va
// `import.meta.env` ga bog'langani uchun Node testidan import qilib
// bo'lmaydi. Batafsil sabab — `branding.ts` boshidagi izoh.
import { toBranding, type PortalBranding } from './branding'
import type { AccountVoucherView } from './accountVoucher'
import {
  accessRequestBody,
  accountVoucherRequestBody,
  type AccountVoucherInput,
  type GrantAccessInput,
} from './requests'

export { safeHttpsUrl, toBranding, welcomeText } from './branding'
export type { PortalBranding, PortalTemplateRaw } from './branding'
export { serializeCredential, hasAccess } from './credential'
export type { GuestCredentialInput } from './credential'
export type { AccountVoucherInput, GrantAccessInput } from './requests'

// ------------------------------------------------------------------ enum'lar

/** `uz.xsib.venue.api.IdentificationMethod` */
export const IDENTIFICATION_METHODS = [
  'SMS_OTP',
  'ROOM_PMS',
  'VOUCHER',
  'SOCIAL',
  'STAFF_MANUAL',
  'DEVICE_MEMO',
] as const
export type IdentificationMethod = (typeof IDENTIFICATION_METHODS)[number]

function isIdentificationMethod(value: string): value is IdentificationMethod {
  return (IDENTIFICATION_METHODS as readonly string[]).includes(value)
}

/**
 * Portal HOZIRCHA qo'llab-quvvatlaydigan usullar — vazifa hujjatida
 * so'ralgan ekranlar aynan shular: SMS OTP va Vaucher.
 *
 * `ROOM_PMS` (xona+familiya), `SOCIAL` va `STAFF_MANUAL` zona tomonidan
 * ruxsat etilgan bo'lishi mumkin, lekin ular uchun ekran QURILMAGAN —
 * vazifa ularni so'ramagan va ularning UI oqimi (masalan `STAFF_MANUAL`
 * xodim tizimga kirgan bo'lishini talab qiladi, mehmon portalida emas)
 * aniq emas. `DEVICE_MEMO` — mustaqil ekran emas, avtomatik "qayta
 * eslash" mexanizmi; bu portalda amalga oshirilmagan (pastdagi izohga
 * qarang).
 */
export const SUPPORTED_METHODS: readonly IdentificationMethod[] = ['SMS_OTP', 'VOUCHER']

/** `uz.xsib.venue.api.IdentificationStrength` */
export const IDENTIFICATION_STRENGTHS = ['SUFFICIENT', 'DELEGATED', 'INSUFFICIENT'] as const
export type IdentificationStrength = (typeof IDENTIFICATION_STRENGTHS)[number]

function isIdentificationStrength(value: string): value is IdentificationStrength {
  return (IDENTIFICATION_STRENGTHS as readonly string[]).includes(value)
}

// -------------------------------------------------------------- AdCampaign

const adCampaignSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  venueId: z.string().nullish(),
  name: z.string(),
  advertiserName: z.string(),
  advertiserPartyId: z.string().nullish(),
  creativeRef: z.string().nullish(),
  rewardBytes: z.number().nullish(),
  rewardSeconds: z.number().nullish(),
  minViewSeconds: z.number(),
  dailyCapPerGuest: z.number(),
  startsAt: z.string(),
  endsAt: z.string().nullish(),
  state: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type AdCampaign = z.infer<typeof adCampaignSchema>

// ------------------------------------------------------- ob'ekt ko'rinishi

/**
 * `venue.portal_template` — ob'ekt sozlagan brend ko'rinishi.
 *
 * ⚠️ Barcha maydonlar IXTIYORIY va bu xato emas: sozlanmagan ob'ekt uchun
 * backend bo'sh shablon qaytaradi (`PortalTemplate.empty`) va portal
 * neytral palitrada ishlaydi. Ya'ni "yo'q" — standart holat, `null`
 * tekshiruvi hamma joyda kerak.
 *
 * ⚠️ Jackson `null` maydonlarni UMUMAN yubormaydi, shuning uchun
 * `nullish()` (null VA undefined) — `nullable()` yetmaydi.
 */
const portalTemplateSchema = z.object({
  logoUrl: z.string().nullish(),
  primaryColor: z.string().nullish(),
  welcomeUz: z.string().nullish(),
  welcomeRu: z.string().nullish(),
  welcomeEn: z.string().nullish(),
  termsUrl: z.string().nullish(),
  supportPhone: z.string().nullish(),
})

// ⚠️ `PortalBranding`, `safeHttpsUrl`, `toBranding`, `welcomeText` —
// `./branding.ts` da (yuqorida qayta eksport qilingan).

// -------------------------------------------------------------- zona ma'lumoti

const zoneOptionsSchema = z.object({
  zoneId: z.string(),
  venueName: z.string(),
  zoneName: z.string(),
  allowedMethods: z.array(z.string()),
  minStrength: z.string(),
  adRequired: z.boolean(),
  campaigns: z.array(adCampaignSchema),
  /** ⚠️ Sozlanmagan ob'ektda YO'Q — `nullish` majburiy */
  template: portalTemplateSchema.nullish(),
})

export interface ZoneOptions {
  zoneId: string
  venueName: string
  zoneName: string
  allowedMethods: IdentificationMethod[]
  minStrength: IdentificationStrength
  adRequired: boolean
  campaigns: AdCampaign[]
  /** `null` = ob'ekt ko'rinishni sozlamagan (Netlink palitrasi) */
  branding: PortalBranding | null
}

/**
 * `GET /api/portal/zone` — portal birinchi chaqiradigan endpoint.
 * `nasIdentifier` MikroTik `$(identity)` — `portalParams.ts` dagi `nasid`.
 */
export async function fetchZoneOptions(nasIdentifier: string): Promise<ZoneOptions> {
  const response = await api.get<unknown>('/api/portal/zone', { params: { nasIdentifier } })
  const parsed = zoneOptionsSchema.parse(response.data)
  return {
    ...parsed,
    // Noma'lum usul (backend yangi enum qo'shsa) jimgina chetlab o'tiladi —
    // yiqilish o'rniga faqat shu usul tugmasi ko'rinmaydi.
    allowedMethods: parsed.allowedMethods.filter(isIdentificationMethod),
    minStrength: isIdentificationStrength(parsed.minStrength) ? parsed.minStrength : 'SUFFICIENT',
    branding: toBranding(parsed.template),
  }
}

// ------------------------------------------------------------------- OTP

const otpIssuedSchema = z.object({
  challengeId: z.string(),
  maskedDestination: z.string(),
  expiresAt: z.string(),
  resendAfterSeconds: z.number(),
  maxAttempts: z.number(),
})
export type OtpIssued = z.infer<typeof otpIssuedSchema>

export interface RequestOtpInput {
  zoneId: string
  phone: string
  mac: string
  requestIp: string | null
  locale: string
}

/** `POST /api/portal/otp` — ⚠️ bu chaqiruv PUL SARFLAYDI (SMS). */
export async function requestOtp(input: RequestOtpInput): Promise<OtpIssued> {
  const response = await api.post<unknown>('/api/portal/otp', {
    zoneId: input.zoneId,
    phone: input.phone,
    mac: input.mac,
    requestIp: input.requestIp,
    locale: input.locale,
  })
  return otpIssuedSchema.parse(response.data)
}

// -------------------------------------------------------------- OpenSession

/*
 * ⚠️ To'liq `GuestSession` sxemasi ATAYLAB YO'Q. Anonim portal
 * endpointlari (`GET /session`, `POST /access`, reklama tugatish) mehmonga
 * to'liq tashrifni — `subscriberLogin`, `subscriberId`, `ip`, `guestId`,
 * `tenantId`, `venueId` — HECH QACHON qaytarmaydi. Backend ularni
 * qisqartirilgan ko'rinishlarga o'giradi (`OpenSessionView`,
 * `PortalAccessView`). Bu yerga to'liq `GuestSession` turini qaytarish
 * o'sha teshiklarni tiklash demakdir.
 */

/**
 * `GET /api/portal/session` javobi — `venue/guestaccess/dto/OpenSessionView.java`.
 *
 * ⚠️ Bu `GuestSession` NING QISQARTIRILGAN SHAKLI, va ataylab shunday.
 * O'sha endpoint anonim, uni chaqirish uchun kerak bo'lgan ikkala qiymat
 * ham ochiq: `zoneId` anonim `GET /api/portal/zone` dan olinadi, `mac` esa
 * bir xil Wi-Fi'da efirda ko'rinadi. Ilgari u to'liq `GuestSession`
 * qaytarardi va abonentning `subscriberLogin`, `subscriberId` hamda `ip`
 * si autentifikatsiyasiz oshkor bo'lardi.
 *
 * Shuning uchun bu yerga yangi maydon qo'shishdan oldin backend'dagi
 * `OpenSessionView` javadoc'ini o'qing — u yerda nima uchun aynan shu
 * beshta maydon qolgani yozilgan.
 */
const openSessionSchema = z.object({
  sessionId: z.string(),
  startedAt: z.string(),
  expiresAt: z.string(),
  adFunded: z.boolean(),
  campaignId: z.string().nullish(),
})
export type OpenSession = z.infer<typeof openSessionSchema>

// ---------------------------------------------------------- GuestCredential

/**
 * Portal tomonidagi kirish ma'lumoti — `venue.api.GuestCredential` (sealed
 * interface, Java record'lar) ning mos keladigan qismi (`SUPPORTED_METHODS`).
 *
 * ⚠️ JSON'da "method"/"type" DISKRIMINATOR MAYDONI YO'Q. `GuestCredential.java`
 * da `@JsonTypeInfo` annotatsiyasi yo'q — Spring Boot 3.4.1 (Jackson 2.18)
 * `sealed interface` + record kombinatsiyasini DEDUCTION orqali, ya'ni JSON
 * ob'ektining MAYDON NOMLARIGA qarab avtomatik farqlaydi. Shuning uchun har
 * bir credential faqat OʻZ record'ining maydonlarini yuboradi — boshqa
 * hech narsa, "usul" yozilmaydi.
 *
 * ⚠️ VOUCHER uchun `identifiedSale` HAR DOIM `false`. Bu maydon "sotuv
 * identifikatsiya bilan qayd etilganmi"ni bildiradi
 * (`docs/09-VENUE-PORTAL.md` §5: anonim karta → INSUFFICIENT, identifikatsiya
 * bilan sotilgan → DELEGATED). Buni FAQAT ob'ekt/sotuv tizimi biladi —
 * anonim mehmon portali bu haqda hech narsa bilmaydi va bila olmaydi. Uni
 * `true` qilib yuborish mehmonning o'zi o'ziga huquqiy kuch berishi
 * bo'lardi, ya'ni №137 talabini soxtalashtirish. Agar zona faqat DELEGATED+
 * kuch talab qilsa va faqat anonim vaucher ruxsat etilgan bo'lsa, backend
 * `InsufficientIdentification` (422) qaytaradi — bu TO'G'RI xatti-harakat,
 * portal buni yashirmaydi.
 */
// ⚠️ `GuestCredentialInput` va `serializeCredential` — `./credential.ts` da
// (yuqorida qayta eksport qilingan).

// ------------------------------------------------------------ GuestAccessResult

/**
 * `POST /api/portal/access` va reklama tugatish javobi —
 * `venue/guestaccess/dto/PortalAccessView.java`.
 *
 * ⚠️ Bu `GuestAccessResult` NING QISQARTIRILGAN, portalga xos shakli, va
 * ataylab shunday. Endpoint anonim (`permitAll`) va uni chaqirish uchun
 * kerak narsalar ochiq: `zoneId` anonim `GET /api/portal/zone` dan, `mac`
 * esa efirdan. Ilgari javob to'liq `GuestAccessResult` edi va u mehmonning
 * `subscriberLogin` ini hamda butun `session` ni (`subscriberId`, `ip`,
 * `guestId`, `tenantId`, …) autentifikatsiyasiz oshkor qilardi. Endi
 * backend faqat portal UI ishlatadigan maydonlarni qaytaradi — yangi maydon
 * qo'shishdan oldin `PortalAccessView` javadoc'ini o'qing.
 *
 * ⚠️ `login` — mehmon MikroTik `/login` formasiga yuboriladigan nom va u
 * REALM bilan keladi (`g.deadbeef.aabbccddeeff@uz-venue.xsib`). X-SIB
 * ichidagi TOZA login (`subscriberLogin`) bu yerdan ATAYLAB olib
 * tashlangan: u MikroTik'ga kerak emas, faqat panel/qidiruvda ishlatiladi.
 *
 * `sessionId`/`campaignId` — reklama oqimini davom ettirish uchun (avval
 * `session.id`/`session.campaignId` ichida kelardi, endi tekis).
 *
 * `.nullish()`: bu servis va portal — IKKI mustaqil deploy artefakti; eski
 * `xsib-venue` ba'zi maydonlarni yubormasligi mumkin va o'shanda portal
 * yiqilmasligi kerak.
 */
const guestAccessResultSchema = z.object({
  sessionId: z.string(),
  login: z.string().nullish(),
  password: z.string().nullish(),
  expiresAt: z.string().nullish(),
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
  adFunded: z.boolean(),
  campaignId: z.string().nullish(),
})
export type GuestAccessResult = z.infer<typeof guestAccessResultSchema>

// ⚠️ `hasAccess` — `./credential.ts` da (yuqorida qayta eksport qilingan).

/**
 * `POST /api/portal/access` — identifikatsiya va kirish.
 *
 * ⚠️ Chaqiruv IDEMPOTENT (bir zona+MAC uchun ochiq tashrif bo'lsa,
 * mavjudining ma'lumoti qaytariladi) — portal so'rovni ikki marta yuborsa
 * (tugma ikki marta bosilsa, tarmoq uzilsa) ikkinchi mehmon yaratilmaydi.
 */
export async function grantAccess(input: GrantAccessInput): Promise<GuestAccessResult> {
  const response = await api.post<unknown>('/api/portal/access', accessRequestBody(input))
  return guestAccessResultSchema.parse(response.data)
}

const accountVoucherSchema = z.object({
  login: z.string(),
  radiusLogin: z.string(),
  hotspotLogin: z.boolean(),
  planName: z.string().nullish(),
  expiresAt: z.string().nullish(),
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
}) satisfies z.ZodType<AccountVoucherView>

/** `POST /api/portal/account-voucher` — karta mavjud abonent hisobiga faollashtiriladi. */
export async function redeemOnAccount(input: AccountVoucherInput): Promise<AccountVoucherView> {
  const response = await api.post<unknown>('/api/portal/account-voucher', accountVoucherRequestBody(input))
  return accountVoucherSchema.parse(response.data)
}

/**
 * `GET /api/portal/session` — ochiq tashrifni tekshirish.
 * Tashrif yo'q bo'lsa `data: null` (normal holat, xato emas).
 *
 * ⚠️ Ilgari bu holat **204 No Content** edi. Envelope kiritilganda 204
 * dan voz kechildi: u ta'rifi bo'yicha tanasiz, ya'ni envelope mijozga
 * yetib bormasdi. `validateStatus` va `status === 204` sharti ATAYLAB
 * saqlandi — deploy oralig'ida eski backend bilan ham ishlasin.
 *
 * ⚠️ Javob turi `OpenSession` — to'liq `GuestSession` EMAS (yuqoridagi
 * `openSessionSchema` izohi). Bu yerda `guestSessionSchema` ni qaytarish
 * eski, xavfsizlik nuqsonli shaklga qaytish demakdir.
 */
export async function findOpenSession(zoneId: string, mac: string): Promise<OpenSession | null> {
  const response = await api.get<unknown>('/api/portal/session', {
    params: { zoneId, mac },
    validateStatus: (status) => status === 200 || status === 204,
  })
  if (response.status === 204 || response.data === null) return null
  return openSessionSchema.parse(response.data)
}

// ----------------------------------------------------------------- reklama

const impressionStartedSchema = z.object({
  impressionId: z.string(),
  nonce: z.string(),
  minViewSeconds: z.number(),
})
export type ImpressionStarted = z.infer<typeof impressionStartedSchema>

/** `POST /api/portal/impressions` — ko'rsatishni SERVER boshlaydi, nonce beradi. */
export async function startImpression(
  sessionId: string,
  campaignId: string,
): Promise<ImpressionStarted> {
  const response = await api.post<unknown>('/api/portal/impressions', null, {
    params: { sessionId, campaignId },
  })
  return impressionStartedSchema.parse(response.data)
}

const adImpressionSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  campaignId: z.string(),
  guestSessionId: z.string(),
  startedAt: z.string(),
  completedAt: z.string().nullish(),
  state: z.string(),
  grantId: z.string().nullish(),
})

const impressionCompletedSchema = z.object({
  impression: adImpressionSchema,
  access: guestAccessResultSchema.nullish(),
})
export type ImpressionCompleted = z.infer<typeof impressionCompletedSchema>

/**
 * `POST /api/portal/impressions/{id}/complete` — reklama bilan
 * moliyalashtiriladigan zonada aynan shu chaqiruv kirishni ochadi.
 *
 * ⚠️ Davomiylik SERVER soati bo'yicha tekshiriladi. Agar hali yetarlicha
 * vaqt o'tmagan bo'lsa 422 (`ImpressionNotCompletable`) qaytadi va NONCE
 * KUYMAYDI — ya'ni bu chaqiruvni battle bir necha soniyadan keyin XUDDI
 * SHU `nonce` bilan qayta urinish mumkin (`GuestAccessServiceImpl.
 * completeImpression`: nonce faqat muvaffaqiyatli yakunlanganda kuyadi).
 */
export async function completeImpression(
  impressionId: string,
  nonce: string,
): Promise<ImpressionCompleted> {
  const response = await api.post<unknown>(`/api/portal/impressions/${impressionId}/complete`, null, {
    params: { nonce },
  })
  return impressionCompletedSchema.parse(response.data)
}
